import { IntegrationData } from "../types"

export const swapcardData: IntegrationData = {
  platform: {
    slug: "swapcard",
    name: "Swapcard",
    description:
      "Turn every attendee into an event promoter with Premagic's native Swapcard integration",
    shortDescription: "Real-time attendee activation for event growth",
    logoUrl:
      "https://cdn.prod.website-files.com/6341448fda79c92372b010a4/63502c47c8e7bdfa7c25aefe_swapcardLogo.svg",
    websiteUrl: "https://www.swapcard.com/",
    category: "event-management",
    tags: ["Event Management", "Real-Time Webhooks"],
    isComingSoon: false,
  },
  heroTitle: "Real-Time Attendee Activation for Event Growth",
  heroDescription:
    "We're excited to announce our native integration with Swapcard, the all-in-one event platform trusted by leading conferences worldwide. This integration transforms your Swapcard attendees into active event promoters – in real-time – driving referrals and boosting registrations through personalized, shareable content.",
  challengeTitle: "The Challenge",
  challengeDescription:
    "You're running your event on Swapcard – one of the most powerful event platforms for conferences, trade shows, and hybrid events. Registrations are coming in. But how do you turn those registrations into a growth engine? Your attendees are excited. They've signed up. But that excitement fades quickly if you don't capture it at the right moment.",
  challengePoints: [
    {
      title: "Excitement Fades Fast",
      description:
        "The best time to get attendees to share your event is immediately after they register – not days later when they've moved on.",
    },
    {
      title: "Manual Poster Creation",
      description:
        "Creating personalized marketing materials for each attendee is time-consuming and requires design skills. Without automation, you can't scale poster creation to your entire attendee base.",
    },
    {
      title: "Missed Real-Time Opportunities",
      description:
        "Without real-time sync, you miss the critical window to capture referrals at peak excitement. Traditional integrations can't keep up with the speed of registration.",
    },
  ],
  capabilities: [
    {
      title: "Auto-Generate Personalized Posters from Swapcard Attendee Data",
      description:
        "Because we sync all attendee information from Swapcard – including profile photos, job titles, companies, and custom fields – we can automatically create pre-filled posters for every registrant.",
      features: [
        "Attendee's full name and job title",
        "Organization and profile photo",
        "Attendee type (Speaker, Sponsor, Exhibitor, etc.)",
        "Custom event branding",
      ],
    },
    {
      title: "Real-Time Sync with Webhooks",
      description:
        "Unlike other integrations, Swapcard + Premagic syncs in real-time. New registration? Premagic is notified instantly via webhook. Profile update? Changes sync automatically within seconds.",
      features: [
        "Instant notification on new registrations",
        "Automatic sync on profile updates",
        "No manual triggers needed",
        "Integration stays current automatically",
      ],
    },
    {
      title: "Launch Campaigns to Drive Sharing and Referrals",
      description:
        "Don't wait for attendees to share on their own. Proactively send poster campaigns to nudge them into action.",
      features: [
        "Instant welcome emails with personalized poster",
        "'Share your attendance' campaigns with one-click social sharing",
        "Reminder sequences leading up to the event",
        "Track which attendees are driving the most referrals",
      ],
    },
    {
      title: "Complete Attendee Roster Sync",
      description:
        "The integration automatically imports your full attendee roster from Swapcard, including all contact information and custom fields.",
      features: [
        "Full name and contact information",
        "Email and phone number with country code",
        "Job title and organization",
        "Profile photos and social network profiles",
      ],
    },
    {
      title: "Smart Attendee Type Recognition",
      description:
        "Swapcard groups are automatically mapped to attendee types, ensuring your posters and galleries automatically reflect each person's role at your event.",
      features: [
        "Attendees → Standard attendee",
        "Speakers → Speaker badge and treatment",
        "Sponsors → Sponsor recognition",
        "Exhibitors → Exhibitor features",
      ],
    },
    {
      title: "Social Profile Import",
      description:
        "Swapcard captures social profiles – and so does Premagic. Social links are automatically imported and available for sharing optimization.",
      features: [
        "LinkedIn, Twitter, Facebook import",
        "Ready for poster personalization",
        "Post-event networking features",
        "Social sharing optimization",
      ],
    },
  ],
  flywheelTitle: "The Real-Time Referral Flywheel",
  flywheelDescription:
    "Here's how it works in practice: Every registration multiplies your reach – in real-time.",
  flywheelSteps: [
    {
      number: 1,
      title: "Register",
      description: "Attendee signs up on Swapcard",
    },
    {
      number: 2,
      title: "Webhook fires",
      description: "Premagic receives instant notification",
    },
    {
      number: 3,
      title: "Auto-generate",
      description: "Personalized poster created in seconds",
    },
    {
      number: 4,
      title: "Campaign triggers",
      description: "Welcome email with poster sent immediately",
    },
    {
      number: 5,
      title: "Share",
      description: "Attendee shares to LinkedIn, Twitter, WhatsApp, email",
    },
    {
      number: 6,
      title: "Discover",
      description: "Their network sees the poster with event details",
    },
    {
      number: 7,
      title: "Register",
      description: "New attendees sign up through shared links",
    },
    {
      number: 8,
      title: "Repeat",
      description: "Webhook fires again, flywheel continues",
    },
  ],
  howItWorksSteps: [
    {
      number: 1,
      title: "Connect",
      description: "Link your Swapcard API credentials to Premagic",
    },
    {
      number: 2,
      title: "Select",
      description:
        "Choose which Swapcard event to connect to your Premagic project",
    },
    {
      number: 3,
      title: "Configure",
      description: "Select which attendee fields and groups to sync",
    },
    {
      number: 4,
      title: "Webhook",
      description: "Real-time sync is automatically enabled",
    },
    {
      number: 5,
      title: "Campaign",
      description: "Send personalized poster campaigns to drive sharing",
    },
    {
      number: 6,
      title: "Track",
      description:
        "Monitor referrals and registration growth in both platforms",
    },
  ],
  capabilitiesTable: [
    {
      feature: "Real-Time Webhooks",
      description:
        "Instant notification on new registrations and profile updates",
    },
    {
      feature: "Pre-filled Posters",
      description:
        "Auto-generate personalized posters from synced attendee data",
    },
    {
      feature: "Campaign Distribution",
      description: "Send poster campaigns to drive sharing and referrals",
    },
    {
      feature: "Full Attendee Sync",
      description:
        "Import names, emails, phones, job titles, companies, and photos",
    },
    {
      feature: "Profile Photo Import",
      description:
        "Use Swapcard profile pictures for poster personalization and facial recognition",
    },
    {
      feature: "Social Profile Sync",
      description: "Import LinkedIn, Twitter, Facebook, and other social links",
    },
    {
      feature: "Attendee Type Mapping",
      description:
        "Automatic recognition of speakers, sponsors, exhibitors, attendees",
    },
    {
      feature: "Group-Based Filtering",
      description: "Sync only specific attendee groups or segments",
    },
    {
      feature: "Custom Field Support",
      description: "Import all Swapcard custom registration fields",
    },
    {
      feature: "Bi-directional Sync",
      description:
        "Push gallery URLs, registration IDs, and face IDs back to Swapcard",
    },
    {
      feature: "Event Document Sync",
      description: "Add Premagic links as event-level documents in Swapcard",
    },
    {
      feature: "AI Photo Distribution",
      description: "Post-event photo delivery powered by synced attendee data",
    },
  ],
  targetAudience: [
    {
      title: "Conference Organizers",
      description:
        "Running large-scale events on Swapcard who want organic registration growth",
    },
    {
      title: "Event Marketing Teams",
      description: "Looking to amplify reach without increasing ad spend",
    },
    {
      title: "Trade Show Managers",
      description: "Who need exhibitors and sponsors to promote the event",
    },
    {
      title: "Hybrid Event Planners",
      description: "Maximizing both in-person and virtual attendance",
    },
    {
      title: "Event Agencies",
      description:
        "Managing client events and delivering measurable registration results",
    },
  ],
  ctaTitle:
    "Ready to Turn Your Swapcard Event Into a Registration Growth Machine?",
  ctaDescription:
    "Your Swapcard attendees are your best marketers. They're already excited about your event. They just need the right tools – delivered at the right moment. With Premagic's Swapcard integration, you can capture the moment, react in real-time, personalize at scale, activate every segment, and drive registrations. Stop letting registrations sit idle. Activate them instantly.",
  seo: {
    title: "Premagic + Swapcard Integration | Real-Time Attendee Activation",
    description:
      "Transform Swapcard attendees into active event promoters with real-time webhooks, personalized posters, and automated campaigns. Drive referrals and boost registrations instantly.",
    keywords: [
      "swapcard",
      "event management",
      "attendee advocacy",
      "event marketing",
      "referral marketing",
      "poster creation",
      "event promotion",
      "registration growth",
      "real-time webhooks",
      "attendee activation",
    ],
  },
}
